import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CaretUpDown, Check, MapPinLine, Plus, CircleNotch, Globe } from "@phosphor-icons/react";
import { toast } from "sonner";

function useDebounce(value, delay = 500) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => { const t = setTimeout(() => setDebounced(value), delay); return () => clearTimeout(t); }, [value, delay]);
  return debounced;
}

/**
 * Combobox for selecting a city with autocomplete + on-the-fly add via Nominatim.
 * Props: value (city id), onChange(cityId), cities (list from /api/cities), refreshCities()
 * If `cities` and `refreshCities` are not provided, the component manages them internally.
 */
export default function CityCombobox({
  value,
  onChange,
  cities: externalCities,
  refreshCities: externalRefresh,
  placeholder = "Seleziona città",
  className = "",
  disabled = false,
  testId = "city-combobox",
}) {
  const [open, setOpen] = useState(false);
  const [localCities, setLocalCities] = useState([]);
  const [query, setQuery] = useState("");
  const debounced = useDebounce(query, 450);
  const [remoteResults, setRemoteResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);

  const cities = externalCities ?? localCities;
  const refresh = externalRefresh ?? (() => api.get("/cities").then((r) => setLocalCities(r.data)));

  useEffect(() => { if (!externalCities) refresh(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!debounced || debounced.trim().length < 2) { setRemoteResults([]); return; }
    // hide Nominatim results if the query already matches a local city name
    const q = debounced.toLowerCase();
    const localMatch = cities.some((c) => c.name.toLowerCase().includes(q));
    if (localMatch && cities.filter((c) => c.name.toLowerCase().includes(q)).length >= 3) {
      setRemoteResults([]); return;
    }
    setSearching(true);
    api.get(`/cities/search?q=${encodeURIComponent(debounced)}`)
      .then((r) => setRemoteResults(r.data || []))
      .catch(() => setRemoteResults([]))
      .finally(() => setSearching(false));
  }, [debounced, cities]);

  const selected = useMemo(() => cities.find((c) => c.id === value), [cities, value]);

  const addAndSelect = async (r) => {
    setAdding(true);
    try {
      const { data } = await api.post("/cities/custom", {
        name: r.name, lat: r.lat, lng: r.lng,
        display_name: r.display_name, place_type: r.type,
      });
      toast.success(data.already_existed ? `${r.name} già presente` : `${r.name} aggiunta`);
      await refresh();
      onChange?.(data.id);
      setOpen(false);
      setQuery("");
      setRemoteResults([]);
    } catch {
      toast.error("Errore nell'aggiunta della città");
    } finally {
      setAdding(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          variant="outline"
          className={`w-full justify-between rounded-none bg-zinc-900 border-white/10 hover:bg-zinc-900 hover:text-zinc-50 font-normal h-10 px-3 ${className}`}
          data-testid={testId}
        >
          <span className="flex items-center gap-2 truncate">
            <MapPinLine size={14} weight="bold" className={selected?.custom ? "text-cyan-400" : "text-lime-400"} />
            <span className="truncate">{selected ? selected.name : placeholder}</span>
            {selected?.custom && <span className="ml-1 text-[9px] font-mono-data text-cyan-400 border border-cyan-400/40 px-1">CUSTOM</span>}
          </span>
          <CaretUpDown size={14} weight="bold" className="text-zinc-500 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-none bg-zinc-950 border-white/10" align="start">
        <Command className="rounded-none bg-zinc-950" shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Cerca città in Italia..."
            className="h-10 font-mono-data text-sm"
            data-testid={`${testId}-input`}
          />
          <CommandList className="max-h-[320px]">
            <CommandEmpty>
              <div className="py-3 text-xs text-zinc-500 font-mono-data">
                {searching ? "Ricerca in corso..." : query.length < 2 ? "Digita per cercare" : "Nessun risultato"}
              </div>
            </CommandEmpty>

            <CommandGroup heading={<span className="text-[10px] font-mono-data text-zinc-500 tracking-widest">CATALOGO</span>}>
              {cities.filter((c) => !query || c.name.toLowerCase().includes(query.toLowerCase())).map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.id}
                  onSelect={() => { onChange?.(c.id); setOpen(false); setQuery(""); }}
                  className="rounded-none aria-selected:bg-white/5 cursor-pointer"
                  data-testid={`${testId}-option-${c.id}`}
                >
                  <MapPinLine size={13} weight="bold" className={`mr-2 ${c.custom ? "text-cyan-400" : "text-lime-400"}`} />
                  <span>{c.name}</span>
                  {c.custom && <span className="ml-2 text-[9px] font-mono-data text-cyan-400 border border-cyan-400/40 px-1">CUSTOM</span>}
                  {value === c.id && <Check size={13} weight="bold" className="ml-auto text-lime-400" />}
                </CommandItem>
              ))}
            </CommandGroup>

            {remoteResults.length > 0 && (
              <>
                <CommandSeparator className="bg-white/5" />
                <CommandGroup heading={
                  <span className="text-[10px] font-mono-data text-cyan-400 tracking-widest flex items-center gap-1.5">
                    <Globe size={11} weight="bold" /> AGGIUNGI DA OPENSTREETMAP
                  </span>
                }>
                  {remoteResults.map((r) => (
                    <CommandItem
                      key={`${r.display_name}-${r.lat}`}
                      value={`nominatim-${r.name}-${r.lat}`}
                      onSelect={() => !adding && addAndSelect(r)}
                      className="rounded-none aria-selected:bg-cyan-500/10 cursor-pointer"
                      data-testid={`${testId}-nominatim-${r.name}`}
                    >
                      {adding ? <CircleNotch size={13} weight="bold" className="animate-spin mr-2 text-cyan-400" /> : <Plus size={13} weight="bold" className="mr-2 text-cyan-400" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm">{r.name} {r.type && <span className="text-[9px] font-mono-data text-zinc-500 ml-1">{r.type}</span>}</div>
                        <div className="text-[10px] text-zinc-500 truncate font-mono-data">{r.display_name}</div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
